import React from 'react';
import styled from 'styled-components';
import {
  BoldSmallButtonText,
  ButtonDropdown,
  Icon,
  IconSize,
  IconType,
  TDropdownElementProps,
  FlexRow,
} from 'ui-toolkit';
import { eApplicationActions, useApplicationContext } from '../../../../state';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { loadOrderAsyncAction } from '../../../../store/order/batchedDispatches';
import { useUpdateOrderTimberHook } from '../../../../API/order/useUpdateOrderHook';
import { useAppDispatch } from '../../../../store/store';
import { eInternalOrderStatus } from '../../../../ui-toolkit/enums/eInternalOrderStatus';
import { IOrderDetailsData } from '../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import type { FCWithChildren } from '../../../../types/FCWithChildren';

const GrayIcon = styled(Icon)`
  color: ${(props) => props.theme.colors.defaultContentText};
`;

const RedRow = styled(FlexRow)<{ disabled?: boolean }>`
  cursor: ${(props) => props.disabled && 'not-allowed'};
  color: ${(props) => (props.disabled ? props.theme.colors.darkGray : props.theme.colors.error)};
`;

const generateChangeStatusItems = (
  onChangeStatusClick: (s: eInternalOrderStatus) => void,
  onCancelOrderClick: () => void,
  disableCancelButton: boolean
): TDropdownElementProps[] => {
  return [
    {
      children: 'Initial Docs Available',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.initial_docs_available),
    },
    {
      children: 'Waiting for Search',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.waiting_for_search),
    },
    {
      children: 'Title Production',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.production),
    },
    {
      children: 'Title Report Available',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.title_report_available),
    },
    {
      children: 'Clearance',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.clearance),
    },
    {
      children: 'Title Clear',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.title_clear),
    },
    {
      children: 'Clear to Close',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.clear_to_close),
    },
    {
      children: 'Signing Scheduled',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.signing_scheduled),
    },
    {
      children: 'Signing Confirmed',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.signing_confirmed),
    },
    {
      children: 'Signing Occurred',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.signing_occurred),
    },
    {
      children: 'Waiting For Funds',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.waiting_for_funds),
    },
    {
      children: 'Ready for Funding',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.ready_to_disburse),
    },
    {
      children: 'Funded',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.funded),
    },
    {
      children: 'Recorded',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.recorded),
    },
    {
      children: 'Funded and Recorded',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.funded_and_recorded),
    },
    {
      children: 'Policy Issued',
      onClick: () => onChangeStatusClick(eInternalOrderStatus.policy_issued),
    },
    {
      children: (
        <>
          <GrayIcon type={IconType.CheckCircleLine} size={IconSize.Small} />
          <BoldSmallButtonText>Order Complete</BoldSmallButtonText>
        </>
      ),
      onClick: () => onChangeStatusClick(eInternalOrderStatus.complete),
    },
    {
      children: (
        <RedRow disabled={disableCancelButton}>
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>Cancel Order</span>
        </RedRow>
      ),
      onClick: () => !disableCancelButton && onCancelOrderClick(),
    },
  ];
};

interface IOrderProgressDropdownProps {
  disableCancelButton: boolean;
  order: IOrderDetailsData;
}

const OrderProgressDropdown: FCWithChildren<IOrderProgressDropdownProps> = ({
  disableCancelButton,
  children,
  order,
}) => {
  const [, dispatch] = useApplicationContext();
  const reduxDispatch = useAppDispatch();
  const { action: doOrderUpdate } = useUpdateOrderTimberHook();

  const onUpdateOrderStatus = async (status: eInternalOrderStatus) => {
    await doOrderUpdate(order, { orderPhase: status });
    await reduxDispatch(loadOrderAsyncAction(order.transaction.id!));
  };

  const showCancelConfirmDialog = () =>
    dispatch({ type: eApplicationActions.ChangeValues, payload: { showCancelConfirmDialog: true } });
  const changeStatusItems = generateChangeStatusItems(
    onUpdateOrderStatus,
    showCancelConfirmDialog,
    disableCancelButton
  );
  return (
    <ButtonDropdown intent={'secondary'} items={changeStatusItems}>
      <BoldSmallButtonText>{children || 'Change Status'}</BoldSmallButtonText>
    </ButtonDropdown>
  );
};

export default OrderProgressDropdown;
